/**
 * Unified input (architecture §7, ui-ux §7): Pointer Events + keyboard → semantic actions and gestures.
 *
 * - Keyboard: Enter/Space → `confirm` (+ `omenTap`), Esc/Backspace → `back`, 1–4 → `prayer1..4`, ←/→ → `prev`/`next`,
 *   `S` → `skip`, `P`/Pause → `pause`. Keys typed into text fields are ignored.
 * - Gestures (ui-ux §7.2/§7.4): movement > 10 px (touch) / 4 px (mouse) → drag; else ≥ 400 ms → long-press; else up →
 *   tap. Two pointers → pinch. A pointerdown inside `.mc-tapzone` is an `omenTap`.
 * - **Omen timing** (combat §7.7): every action carries `presentMs` = the instant that was ON SCREEN at
 *   `event.timeStamp`: the last rendered `presentMs` + (timeStamp − that frame's rAF timestamp) × timeScale, clamped to
 *   the logic frontier `simMs` (0 extra while paused).
 */
import type { Clock, Input, InputAction, PointerInfo } from '../shared';

/** ui-ux §7.2 thresholds (F3 mirrors them in `src/ui/input/constants.ts`). */
export const LONG_PRESS_MS = 400;
export const TAP_SLOP_TOUCH = 10;
export const TAP_SLOP_MOUSE = 4;

type ActionEvent = { presentMs: number; simMs: number; timeStamp: number };
type PointerKind = 'tap' | 'longPress' | 'drag' | 'pinch';

/** The displayed instant at `timeStamp` (pure; unit-tested). */
export function displayedInstant(timeStamp: number, frame: { rafTs: number; presentMs: number }, clock: { paused: boolean; timeScale: number; simMs: number }): number {
  if (clock.paused || !Number.isFinite(frame.rafTs)) return Math.min(frame.presentMs, clock.simMs);
  const dt = Math.max(0, timeStamp - frame.rafTs) * clock.timeScale;
  return Math.min(frame.presentMs + dt, clock.simMs);
}

interface Track { id: number; type: string; x0: number; y0: number; x: number; y: number; t0: number; target: EventTarget | null; dragging: boolean; long: boolean; timer: ReturnType<typeof setTimeout> | null }

const TEXT_INPUT = /^(INPUT|TEXTAREA|SELECT)$/;
const isTextTarget = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement && (TEXT_INPUT.test(t.tagName) || t.isContentEditable);
const isActivatable = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement && (!!t.closest('button, a[href], [role="button"], [role="tab"], summary') || isTextTarget(t));

export class AppInput implements Input {
  private readonly actions = new Map<InputAction, Set<(e: ActionEvent) => void>>();
  private readonly pointers = new Map<PointerKind, Set<(e: PointerInfo) => void>>();
  private readonly tracks = new Map<number, Track>();
  private pinchStart: number | null = null;
  private frame = { rafTs: Number.NaN, presentMs: 0 };
  private readonly listeners: Array<() => void> = [];
  /** Called on any user input (power policy, audio unlock). */
  onAny: ((e: Event) => void) | null = null;
  /** Debug: F2 toggles the perf overlay. */
  onDebugKey: ((key: string) => void) | null = null;

  constructor(private readonly clock: Clock) {}

  attach(target: Window = window): void {
    const add = <K extends keyof WindowEventMap>(type: K, fn: (e: WindowEventMap[K]) => void, opts?: AddEventListenerOptions): void => {
      target.addEventListener(type, fn as EventListener, opts);
      this.listeners.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };
    add('keydown', (e) => this.onKey(e));
    add('pointerdown', (e) => this.onDown(e), { passive: true });
    add('pointermove', (e) => this.onMove(e), { passive: true });
    add('pointerup', (e) => this.onUp(e, false), { passive: true });
    add('pointercancel', (e) => this.onUp(e, true), { passive: true });
    add('wheel', (e) => this.onAny?.(e), { passive: true });
    // iOS needs a passive touchstart listener on the document for :active (ui-ux §7.1).
    const ts = (): void => {};
    document.addEventListener('touchstart', ts, { passive: true });
    this.listeners.push(() => document.removeEventListener('touchstart', ts));
  }

  detach(): void { for (const off of this.listeners.splice(0)) off(); }

  on(action: InputAction, fn: (e: ActionEvent) => void): () => void {
    let set = this.actions.get(action);
    if (!set) { set = new Set(); this.actions.set(action, set); }
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  onPointer(kind: PointerKind, fn: (e: PointerInfo) => void): () => void {
    let set = this.pointers.get(kind);
    if (!set) { set = new Set(); this.pointers.set(kind, set); }
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  /** Called by the loop after presentation: the rAF timestamp and the instant just shown. */
  markFrame(rafTs: number, presentMs: number): void { this.frame = { rafTs, presentMs }; }

  endFrame(): void { /* per-frame state is event-driven; kept for the contract (§2.3 calls it every frame) */ }

  /** The instant that was on screen at `timeStamp` (omen judging). */
  presentAt(timeStamp: number): number { return displayedInstant(timeStamp, this.frame, this.clock); }

  /** Emits a semantic action (also used by on-screen controls that mirror keys). Returns true if anyone listened. */
  emit(action: InputAction, timeStamp: number): boolean {
    const set = this.actions.get(action);
    if (!set || set.size === 0) return false;
    const e: ActionEvent = { presentMs: this.presentAt(timeStamp), simMs: this.clock.simMs, timeStamp };
    for (const fn of [...set]) {
      try { fn(e); } catch (err) { console.error(`[input] ${action} handler threw`, err); }
    }
    return true;
  }

  /** Listener count for an action (the app wires `back` to the router only when no screen handles it itself). */
  listening(action: InputAction): number { return this.actions.get(action)?.size ?? 0; }

  private onKey(e: KeyboardEvent): void {
    this.onAny?.(e);
    if (e.defaultPrevented || e.ctrlKey || e.metaKey) return;
    const inText = isTextTarget(e.target);
    if (e.key === 'F2') { this.onDebugKey?.('F2'); return; }
    if (inText) { if (e.key === 'Escape') this.emit('back', e.timeStamp); return; }
    if (e.repeat && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    switch (e.key) {
      case 'Enter': case ' ':
        if (isActivatable(e.target)) return;           // native activation of the focused control
        this.emit('omenTap', e.timeStamp);
        this.emit('confirm', e.timeStamp);
        if (e.key === ' ') e.preventDefault();
        return;
      case 'Escape': case 'Backspace': this.emit('back', e.timeStamp); return;
      case '1': case '2': case '3': case '4': this.emit(`prayer${e.key}` as InputAction, e.timeStamp); return;
      case 'ArrowLeft': this.emit('prev', e.timeStamp); return;
      case 'ArrowRight': this.emit('next', e.timeStamp); return;
      case 's': case 'S': case 'ы': case 'Ы': this.emit('skip', e.timeStamp); return;
      case 'Pause': case 'p': case 'P': case 'з': case 'З': this.emit('pause', e.timeStamp); return;
      default: return;
    }
  }

  private info(t: Track, e: PointerEvent, dx: number, dy: number, scale = 1): PointerInfo {
    return { x: e.clientX, y: e.clientY, dx, dy, scale, target: t.target, timeStamp: e.timeStamp };
  }

  private fire(kind: PointerKind, info: PointerInfo): void {
    const set = this.pointers.get(kind);
    if (!set) return;
    for (const fn of [...set]) {
      try { fn(info); } catch (err) { console.error(`[input] ${kind} handler threw`, err); }
    }
  }

  private onDown(e: PointerEvent): void {
    this.onAny?.(e);
    const t: Track = { id: e.pointerId, type: e.pointerType, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, t0: e.timeStamp, target: e.target, dragging: false, long: false, timer: null };
    this.tracks.set(e.pointerId, t);
    if (e.target instanceof Element && e.target.closest('.mc-tapzone')) this.emit('omenTap', e.timeStamp);
    if (this.tracks.size === 2) {
      const [a, b] = [...this.tracks.values()];
      this.pinchStart = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      for (const tr of this.tracks.values()) if (tr.timer) { clearTimeout(tr.timer); tr.timer = null; }
      return;
    }
    t.timer = setTimeout(() => {
      t.timer = null;
      if (!t.dragging && this.tracks.get(t.id) === t && this.tracks.size === 1) {
        t.long = true;
        this.fire('longPress', { x: t.x, y: t.y, dx: 0, dy: 0, scale: 1, target: t.target, timeStamp: t.t0 + LONG_PRESS_MS });
      }
    }, LONG_PRESS_MS);
  }

  private onMove(e: PointerEvent): void {
    const t = this.tracks.get(e.pointerId);
    if (!t) return;
    const dx = e.clientX - t.x;
    const dy = e.clientY - t.y;
    t.x = e.clientX; t.y = e.clientY;
    if (this.tracks.size >= 2 && this.pinchStart !== null) {
      const [a, b] = [...this.tracks.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      this.fire('pinch', this.info(t, e, dx, dy, d / this.pinchStart));
      return;
    }
    const slop = t.type === 'mouse' ? TAP_SLOP_MOUSE : TAP_SLOP_TOUCH;
    if (!t.dragging && Math.hypot(t.x - t.x0, t.y - t.y0) > slop && !t.long) {
      t.dragging = true;
      if (t.timer) { clearTimeout(t.timer); t.timer = null; }
    }
    if (t.dragging) this.fire('drag', this.info(t, e, dx, dy));
  }

  private onUp(e: PointerEvent, cancelled: boolean): void {
    const t = this.tracks.get(e.pointerId);
    if (!t) return;
    this.tracks.delete(e.pointerId);
    if (t.timer) { clearTimeout(t.timer); t.timer = null; }
    if (this.tracks.size < 2) this.pinchStart = null;
    if (cancelled || t.dragging || t.long) return;
    if (e.timeStamp - t.t0 < LONG_PRESS_MS) this.fire('tap', this.info(t, e, 0, 0));
  }
}
